import { Directive, effect, EmbeddedViewRef, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';

import { PermissionService } from '../../permission.service';
import { PermissionAction } from '../enums/permission-action.enum';

/**
 * A structural directive that conditionally renders content based on
 * if the current user has the passed project permissions
 *
 * @example Single permission:
 * ```html
 * <div *permissionIf="'files_move';project:'projectIdString'">
 * ```
 *
 * @example Multiple permissions:
 * ```html
 * <div *permissionIf="['files_move', 'analyses_create_job'];project:'projectIdString'">
 * ```
 *
 * @example configure to check 'any' or 'all permissions:
 * ```html
 * <div *permissionIf="['files_move', 'analyses_create_job']; project:'projectIdString'; type: 'any'">
 * ```
 *
 * @see [Angular Structural Directive Syntax Reference](https://angular.dev/guide/directives/structural-directives#structural-directive-syntax-reference)
 */
@Directive({
  selector: '[permissionIf]',
})
export class PermissionIfDirective {
  private templateRef = inject(TemplateRef);
  private viewContainerRef = inject(ViewContainerRef);
  private permissionQueryService = inject(PermissionService);
  private embeddedViewRef: EmbeddedViewRef<unknown>;

  permissionQuery = injectQuery(() =>
    this.permissionQueryService.permissionsForProjectQueryOptions(this.permissionIfProject),
  );
  permissionIf = input([], { transform: this.parsePassedPermissions });

  parsePassedPermissions(
    value: PermissionAction[] | PermissionAction | undefined | `${PermissionAction}` | `${PermissionAction}`[],
  ): PermissionAction[] {
    if (!value) {
      return [];
    }

    if (typeof value === 'string') {
      return [value as unknown as PermissionAction];
    }

    return value as unknown as PermissionAction[];
  }

  permissionIfProject = input<string>();
  permissionIfType = input<'any' | 'all'>('all');

  updateTemplate = effect(() => {
    const userPermissions = this.permissionQuery.data();

    if (!userPermissions) {
      return;
    }

    const permissionsToCheck = this.permissionIf();
    const userPermissionsSet = new Set(userPermissions);

    const hasSomePermissions = permissionsToCheck.some((permission) => userPermissionsSet.has(permission));
    const hasAllPermissions = permissionsToCheck.every((permission) => userPermissionsSet.has(permission));

    const checkPassed = this.permissionIfType() === 'all' ? hasAllPermissions : hasSomePermissions;

    const shouldRender = checkPassed || permissionsToCheck.length === 0;

    if (shouldRender) {
      const embeddedViewAlreadyCreated = Boolean(this.embeddedViewRef);

      if (embeddedViewAlreadyCreated) {
        this.embeddedViewRef.detectChanges();
      } else {
        this.embeddedViewRef = this.viewContainerRef.createEmbeddedView(this.templateRef);
      }
    } else {
      this.viewContainerRef.clear();
    }
  });
}
